import PropTypes from 'prop-types';
import { isEmpty, omit, partition } from 'lodash';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { formatRelayEventDataValue, relayEventDataKeyLabel } from 'common/constants/relayOrders';

import formatRelayDate from '../utils/relayDates';

/**
 * Event detail, minus the keys an operator must not be shown (POM-6099 UI review).
 *
 * `ertUuid` is an opaque DynamoDB handle to a record holding PHI. It says nothing an operator
 * reading a timeline can act on, and the summary no longer shows it either, so showing it here
 * would be the one remaining way to lift it out of the UI. Filtered at RENDER, not in the
 * service: the ledger keeps the value, which is what makes an ERT record findable during an
 * incident by someone whose IAM already permits it.
 *
 * An entry left with nothing but hidden keys renders empty rather than as '{}'.
 *
 * POM-6279 P5: the keys a FAX relay writes (relayEventDataKeyLabel) render as "Label: value",
 * in the order the event carries them. Every other key keeps today's raw JSON, after them, so an
 * unknown key is never relabelled by guesswork and never dropped.
 */
const TIMELINE_HIDDEN_DETAIL_KEYS = ['ertUuid'];

const formatEventDetail = (eventData) => {
  if (!eventData) return '';

  const shown = omit(eventData, TIMELINE_HIDDEN_DETAIL_KEYS);
  const [labelled, raw] = partition(Object.keys(shown), (key) => relayEventDataKeyLabel(key));

  const parts = labelled.map(
    (key) => `${relayEventDataKeyLabel(key)}: ${formatRelayEventDataValue(key, shown[key])}`
  );

  if (!isEmpty(raw)) {
    parts.push(JSON.stringify(omit(shown, labelled)));
  }

  return parts.join('; ');
};

/**
 * Events about the PATIENT rather than the order (POM-6279 UI review): onboarding, not delivery.
 * Hidden at RENDER, like TIMELINE_HIDDEN_DETAIL_KEYS — the ledger keeps them.
 */
const TIMELINE_HIDDEN_EVENT_TYPES = new Set(['PATIENT_QUALIFIED']);

/**
 * POM-6099 — a relay order's event ledger, rendered in the order it is given (core returns it
 * oldest-first; this component does not re-sort). Presentational only: props in, markup out, no
 * Redux, so the Non-Routable fax detail view can render the same ledger.
 */
const RelayEventTimeline = ({ events, hasMoreEvents }) => (
  <Box sx={{ pt: 2 }}>
    {hasMoreEvents && (
      <Alert severity='warning' sx={{ mb: 2 }}>
        This order has more history than can be shown. The earliest events are not shown, including
        the record of the order being received.
      </Alert>
    )}
    <Table size='small' aria-label='relayed order timeline'>
      <TableHead>
        <TableRow>
          <TableCell>When</TableCell>
          <TableCell>Event</TableCell>
          {/* Deliberately headed "Recorded by" and rendered as the raw token. Both the partner
              REST path and a prescriber calling the API directly record PARTNER, so any
              friendlier wording would be wrong half the time. */}
          <TableCell>Recorded by</TableCell>
          <TableCell>HTTP</TableCell>
          <TableCell>Reference</TableCell>
          <TableCell>Detail</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {/* Not deduplicated: repeated rows are real. A partner that tried to cancel five times
            wrote five rows, and each is a clinical signal. */}
        {events
          .filter((relayEvent) => !TIMELINE_HIDDEN_EVENT_TYPES.has(relayEvent.eventType))
          .map((relayEvent) => (
            <TableRow key={relayEvent.eventId}>
              <TableCell>{formatRelayDate(relayEvent.createdTimestamp)}</TableCell>
              <TableCell>{relayEvent.eventType}</TableCell>
              <TableCell>{relayEvent.pomUserId || relayEvent.actor}</TableCell>
              <TableCell>{relayEvent.httpStatus ?? ''}</TableCell>
              <TableCell>{relayEvent.vendorReference || ''}</TableCell>
              {/* event_data is NOT guaranteed PHI-free: MANUAL_NOTE carries operator free text.
                It renders here, for a sys-admin, and goes nowhere else — no export, no clipboard
                helper, no console. */}
              <TableCell sx={{ wordBreak: 'break-all' }}>
                {formatEventDetail(relayEvent.eventData)}
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  </Box>
);

RelayEventTimeline.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      eventId: PropTypes.string.isRequired,
      eventType: PropTypes.string,
      actor: PropTypes.string,
      pomUserId: PropTypes.string,
      httpStatus: PropTypes.number,
      vendorReference: PropTypes.string,
      eventData: PropTypes.shape({}),
      createdTimestamp: PropTypes.string,
    })
  ),
  hasMoreEvents: PropTypes.bool,
};

RelayEventTimeline.defaultProps = { events: [], hasMoreEvents: false };

export default RelayEventTimeline;
